import { useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';

import useIsMobile from 'hooks/useIsMobile';
import { colors } from 'styles/theme/colors';
import CaretDown from 'svg/CaretDown';

import { SharedRecordTab } from './buildSharedRecordTabs';
import useSharedRecordTabs from './useSharedRecordTabs';

const formatOptionLabel = (tab: SharedRecordTab) => {
  const name = tab.label.replace(/\s*Record$/, '').trim();
  const displayName = name || 'Record';
  return tab.subtitle ? `${displayName} - ${tab.subtitle} Record` : `${displayName} - Record`;
};

const LinkedRecordsDropdown = () => {
  const { displayTabs, activeLocationEpisodeId, handleTabClick } = useSharedRecordTabs();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  if (!isMobile) return null;
  if (displayTabs.length === 0) return null;

  const activeTab = displayTabs.find((t) => t.locationEpisodeId === activeLocationEpisodeId) ?? displayTabs[0];
  const linkedCount = displayTabs.length;

  const handleSelect = (tab: SharedRecordTab) => {
    setOpen(false);
    handleTabClick(tab);
  };

  return (
    <Wrapper ref={wrapperRef} data-cy='linked-records-dropdown'>
      <Trigger type='button' onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <TriggerText>
          <TriggerSubtitle>{activeTab.subtitle}</TriggerSubtitle>
          <TriggerLabel>{activeTab.label}</TriggerLabel>
        </TriggerText>
        <TriggerRight>
          <CountPill>
            {linkedCount} {linkedCount === 1 ? 'Record' : 'Records'}
          </CountPill>
          <Caret $open={open}>
            <CaretDown height={12} color={colors.black75} />
          </Caret>
        </TriggerRight>
      </Trigger>
      {open && (
        <Menu role='listbox'>
          {displayTabs.map((tab) => {
            const isActive = tab.locationEpisodeId === activeLocationEpisodeId;
            return (
              <Option
                key={tab.key}
                type='button'
                role='option'
                aria-selected={isActive}
                $active={isActive}
                onClick={() => handleSelect(tab)}
                data-cy='shared-record-dropdown-option'>
                {formatOptionLabel(tab)}
              </Option>
            );
          })}
        </Menu>
      )}
    </Wrapper>
  );
};

export default LinkedRecordsDropdown;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  padding: 8px 25px 8px 0;
`;

const Trigger = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background-color: ${colors.white};
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  padding: 10px 16px;
  cursor: pointer;
  min-height: 48px;
  text-align: left;

  &:hover {
    background-color: ${colors.black03};
  }
`;

const TriggerText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 4px;
`;

const TriggerSubtitle = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${colors.black50};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  line-height: 16px;
`;

const TriggerLabel = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${colors.black};
  line-height: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TriggerRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const CountPill = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: ${colors.white};
  background-color: ${colors.primaryBlue};
  border-radius: 9999px;
  line-height: 16px;
  padding: 2px 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Caret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% - 4px);
  left: 0;
  right: 25px;
  background-color: ${colors.white};
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  box-shadow: 0 4px 16px ${colors.boxShadow};
  z-index: 20;
  overflow: hidden;
`;

const Option = styled.button<{ $active: boolean }>`
  width: 100%;
  border: none;
  background-color: ${({ $active }) => ($active ? colors.primaryBlue10 : 'transparent')};
  cursor: pointer;
  padding: 12px 16px;
  text-align: left;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  color: ${colors.black};
  border-bottom: 1px solid ${colors.black05};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${({ $active }) => ($active ? colors.primaryBlue10 : colors.black05)};
  }
`;
